import type { ConferenceCatalog, RouteLocation } from '../data/schema';

export interface NavigationItem {
  label: string;
  shortLabel: string;
  href: string;
  family: 'home' | 'read' | 'threads' | 'explore' | 'search' | 'study' | 'fsy';
}

export const PRIMARY_NAVIGATION: NavigationItem[] = [
  { label: 'Home', shortLabel: 'Home', href: '/', family: 'home' },
  { label: 'Read', shortLabel: 'Read', href: '/read', family: 'read' },
  { label: 'Threads', shortLabel: 'Threads', href: '/threads', family: 'threads' },
  { label: 'Explore', shortLabel: 'Explore', href: '/explore', family: 'explore' },
  { label: 'Search', shortLabel: 'Search', href: '/search', family: 'search' },
  { label: 'My Study', shortLabel: 'Study', href: '/study', family: 'study' },
  { label: 'FSY', shortLabel: 'FSY', href: '/fsy', family: 'fsy' },
];

function routeFamily(route: RouteLocation): NavigationItem['family'] | null {
  if (route.name === 'home') return 'home';
  if (route.name.startsWith('read-') || route.name === 'scripture') return 'read';
  if (route.name.startsWith('thread')) return 'threads';
  if (route.name.startsWith('explore')) return 'explore';
  if (route.name === 'search') return 'search';
  if (route.name === 'study') return 'study';
  if (route.name.startsWith('fsy-')) return 'fsy';
  return null;
}

function createBrand(conferenceTitle: string): HTMLAnchorElement {
  const brand = document.createElement('a');
  brand.className = 'brand';
  brand.href = '/';
  brand.dataset.router = '';
  brand.setAttribute('aria-label', 'Conference Study home');

  const mark = document.createElement('span');
  mark.className = 'brand__mark';
  mark.setAttribute('aria-hidden', 'true');
  mark.textContent = 'CS';

  const copy = document.createElement('span');
  copy.className = 'brand__copy';
  const name = document.createElement('strong');
  name.textContent = 'Conference Study';
  const conference = document.createElement('small');
  conference.textContent = conferenceTitle;
  copy.append(name, conference);
  brand.append(mark, copy);
  return brand;
}

function createNavigation(activeFamily: NavigationItem['family'] | null): HTMLElement {
  const navigation = document.createElement('nav');
  navigation.className = 'primary-nav';
  navigation.setAttribute('aria-label', 'Primary');

  for (const item of PRIMARY_NAVIGATION) {
    const link = document.createElement('a');
    link.href = item.href;
    link.dataset.router = '';
    link.dataset.destination = item.family;
    link.setAttribute('aria-label', item.label);

    const longLabel = document.createElement('span');
    longLabel.className = 'primary-nav__long-label';
    longLabel.textContent = item.label;
    const shortLabel = document.createElement('span');
    shortLabel.className = 'primary-nav__short-label';
    shortLabel.setAttribute('aria-hidden', 'true');
    shortLabel.textContent = item.shortLabel;
    link.append(longLabel, shortLabel);

    if (item.family === activeFamily) {
      link.setAttribute('aria-current', 'page');
    }
    navigation.append(link);
  }

  return navigation;
}

export function createAppHeader(
  route: RouteLocation,
  catalog?: ConferenceCatalog,
  selectedConferenceId?: string,
  onSelect?: (conferenceId: string) => void,
): HTMLElement {
  const header = document.createElement('header');
  header.className = 'app-header';

  const inner = document.createElement('div');
  inner.className = 'app-header__inner';
  const activeConference = catalog?.conferences.find(
    (conference) => conference.conferenceId === selectedConferenceId,
  );
  inner.append(
    createBrand(activeConference?.title ?? 'General Conference'),
  );
  if (catalog && selectedConferenceId && onSelect) {
    const label = document.createElement('label');
    label.className = 'conference-switcher';
    const prompt = document.createElement('span');
    prompt.textContent = 'Conference';
    const select = document.createElement('select');
    select.setAttribute('aria-label', 'Choose conference');
    for (const conference of catalog.conferences) {
      const option = new Option(conference.title, conference.conferenceId);
      option.selected = conference.conferenceId === selectedConferenceId;
      select.append(option);
    }
    select.addEventListener('change', () => onSelect(select.value));
    label.append(prompt, select);
    inner.append(label);
  }
  inner.append(createNavigation(routeFamily(route)));
  header.append(inner);
  return header;
}
